/* eslint-disable @typescript-eslint/no-explicit-any */
import { AuthProvider } from 'react-admin';
import { logger } from '@musedrops/shared';
import { isAuthorizedAdmin } from '../data/user';
import { supabase } from './supabase';

async function checkAdminAuthorization(
  email: string | null | undefined
): Promise<boolean> {
  if (!email) {
    return false;
  }

  try {
    return await isAuthorizedAdmin(email);
  } catch (error) {
    logger.error('Failed to check admin authorization', { email, error });
    return false;
  }
}

export const authProvider: AuthProvider = {
  async login({
    provider,
    email,
    password,
  }: {
    provider?: string;
    email?: string;
    password?: string;
  }) {
    if (provider === 'google') {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      return Promise.resolve();
    }

    if (email && password) {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        throw new Error(error.message);
      }

      return Promise.resolve();
    }

    throw new Error(
      'Please provide either Google OAuth or email/password credentials'
    );
  },

  async logout() {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
    return '/login';
  },

  async checkError(error: any) {
    if (error?.status === 401 || error?.status === 403) {
      return Promise.reject();
    }
    return Promise.resolve();
  },

  async checkAuth() {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error || !session) {
      throw new Error('Not authenticated');
    }

    const isAuthorized = await checkAdminAuthorization(session.user.email);
    if (!isAuthorized) {
      throw new Error('User not found or not authorized');
    }

    return Promise.resolve();
  },

  async getPermissions() {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      return Promise.resolve([]);
    }

    // All authenticated users in the admins table have full permissions
    const isAuthorized = await checkAdminAuthorization(session.user.email);
    return Promise.resolve(isAuthorized ? 'admin' : 'viewer');
  },

  async getIdentity() {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session?.user?.email) {
      throw new Error('Not authenticated');
    }

    const isAuthorized = await checkAdminAuthorization(session.user.email);
    if (!isAuthorized) {
      throw new Error('User not authorized');
    }

    return {
      id: session.user.email,
      email: session.user.email,
    };
  },
};
